import type { ToothBasisSeriesName } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { ToothSliceGeometry, ToothSliceOptionsBySeries } from '../config';
import {
  DEFAULT_TOOTH_SLICE_OPTIONS_BY_SERIES,
  TOOTH_BASIS_SERIES_ORDER,
} from '../config';

import { makeToothBasisSeriesRequest } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

export const makeToothBasisSeriesSourceKey = ({
  input,
  spacingOutMm,
}: {
  input: ToothSliceGeometry;
  spacingOutMm?: number;
}) =>
  JSON.stringify({
    toothID: input.toothID,
    geometrySource: input.geometrySource,
    request: makeToothBasisSeriesRequest({ input, spacingOutMm }),
  });

const serialiseSliceOptions = (options: ToothSliceOptionsBySeries) =>
  TOOTH_BASIS_SERIES_ORDER.map(
    (seriesName) =>
      `${options[seriesName].sliceStepMm}/${options[seriesName].thicknessMm}`,
  ).join(',');

const DEFAULT_SLICE_OPTIONS_KEY = serialiseSliceOptions(
  DEFAULT_TOOTH_SLICE_OPTIONS_BY_SERIES,
);

export const makeToothBasisSeriesCacheKey = ({
  input,
  sliceOptions,
  spacingOutMm,
}: {
  input: ToothSliceGeometry;
  sliceOptions?: ToothSliceOptionsBySeries;
  spacingOutMm?: number;
}) => {
  const optionsKey = sliceOptions
    ? serialiseSliceOptions(sliceOptions)
    : DEFAULT_SLICE_OPTIONS_KEY;
  const base = `${input.toothID}:${input.volumeFileID}:${
    input.geometrySource
  }:${spacingOutMm ?? 'default'}`;

  return optionsKey === DEFAULT_SLICE_OPTIONS_KEY
    ? base
    : `${base}:${optionsKey}`;
};

export const makeToothBasisSeriesProjectionKeys = ({
  input,
  sliceOptions = DEFAULT_TOOTH_SLICE_OPTIONS_BY_SERIES,
  spacingOutMm,
}: {
  input: ToothSliceGeometry;
  sliceOptions?: ToothSliceOptionsBySeries;
  spacingOutMm?: number;
}): Record<ToothBasisSeriesName, string> => {
  const geometryKey = makeToothBasisSeriesSourceKey({ input, spacingOutMm });

  return Object.fromEntries(
    TOOTH_BASIS_SERIES_ORDER.map((seriesName) => [
      seriesName,
      `${geometryKey}:${seriesName}:${sliceOptions[seriesName].sliceStepMm}/${sliceOptions[seriesName].thicknessMm}`,
    ]),
  ) as Record<ToothBasisSeriesName, string>;
};
